import React, { useState, useEffect } from "react";
import "./StudentImageUpload.css";
import { toast } from 'react-toastify';
import { useAuth } from "../../AuthContext";
import configData from '../../configure';
import { uploadFileToCampusFileSystem, replaceFileContent } from "../../services/CampusFileSystemService";

const StudentImageUpload = ({ selectedUser, onImageUploaded }) => {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [isUpdating, setIsUpdating] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const userId = selectedUser?.studentInfo?.userId || selectedUser?.userId;
  const { axios, token, orgId } = useAuth();

  const fetchPassportPhoto = async () => {
    if (!userId) return;
    try {
      const authHeader = token && token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      const response = await axios.get(
        `${configData.CAMPUS_FILE_GET_BY_ENTITY}/${userId}?entityType=USER&generateDownloadUrls=true`,
        {
          headers: {
            Authorization: authHeader,
            accept: 'application/json',
          },
        }
      );
  
      console.log('🖼️ fetchPassportPhoto raw response.data:', response.data);
      const docs = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.items)
          ? response.data.items
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
      console.log('🖼️ fetchPassportPhoto parsed docs:', docs);

      const isPassportPhoto = (doc) => {
        const cat = (doc.fileCategory || doc.category || doc.documentType || doc.fileType || doc.type || '').toUpperCase();
        return cat === 'PASSPORT_PHOTO' || cat === 'PASSPORTPHOTO' || cat.includes('PASSPORT') || cat.includes('PHOTO') || cat.includes('PROFILE');
      };

      const passportPhoto = docs.find(isPassportPhoto) || docs[0];
      console.log('🖼️ fetchPassportPhoto found photo:', passportPhoto);

      if (passportPhoto) {
        const photoUrl = passportPhoto.downloadUrl || passportPhoto.documentUrl || passportPhoto.fileUrl || passportPhoto.url || passportPhoto.s3Url || passportPhoto.path || null;
        setDocumentId(passportPhoto.fileId || passportPhoto.id);
        setCurrentImageUrl(photoUrl);
        setIsUpdating(true);
        if (onImageUploaded && photoUrl) {
          onImageUploaded({ downloadUrl: photoUrl, documentUrl: photoUrl, ...passportPhoto }, true);
        }
      } else {
        setDocumentId(null);
        setCurrentImageUrl(null);
        setIsUpdating(false);
      }
    } catch (err) {
      console.error('Error fetching passport photo:', err);
      setCurrentImageUrl(null);
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    fetchPassportPhoto();
  }, [userId]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const tempImageUrl = URL.createObjectURL(selectedFile);
      console.log("Temporary Preview URL:", tempImageUrl);
      if (onImageUploaded) {
        onImageUploaded({ tempUrl: tempImageUrl }, false);
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please select an image file");
      setStatus("failed");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Unsupported file format. Please upload a JPG or PNG image.");
      setStatus("failed");
      return;
    }

    setStatus("loading");

    try {
      const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
      let record;

      if (isUpdating && documentId) {
        record = await replaceFileContent(documentId, file, {
          uploadedBy: "admin",
          token: rawToken,
        });
      } else {
        record = await uploadFileToCampusFileSystem(file, {
          entityType: "USER",
          entityId: userId,
          orgId: selectedUser?.user?.orgId || orgId || "1",
          category: "PASSPORT_PHOTO",
          uploadedBy: "admin",
          token: rawToken,
        });
      }

      console.log("Upload successful, Response Data:", record);
      setStatus("succeeded");
      toast.success(`Profile image ${isUpdating ? "updated" : "uploaded"} successfully!`);

      await fetchPassportPhoto();

      if (onImageUploaded) {
        const finalUrl = record?.downloadUrl || record?.documentUrl || record?.fileUrl || record?.url;
        onImageUploaded(record, true);
      }

      setFile(null);
      setTimeout(() => {
        setStatus("idle");
      }, 3000);
    } catch (err) {
      console.error("Upload Error:", err);
      if (err.response) {
        console.error("Upload Error - Status:", err.response.status);
        console.error("Upload Error - Response Data:", err.response.data);
        console.error("Upload Error - Headers:", err.response.headers);
      } else if (err.request) {
        console.error("Upload Error - No response received. Request:", err.request);
      } else {
        console.error("Upload Error - Message:", err.message);
      }
      setStatus("failed");
      toast.error(err.response?.data?.message || "Failed to upload image");
    }
  };

  return (
    <div className="image-upload-section">
      <div className="image-upload-container">
        <input
          type="file"
          id="uploadImage"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden-input"
        />
        <label htmlFor="uploadImage" className="upload-image-button">
          {file ? "Change Image" : currentImageUrl ? "Change Image" : "Select Image"}
        </label>
        {file && (
          <button
            className="upload-button"
            onClick={handleUpload}
            disabled={status === "loading"}
          >
            {status === "loading" ? "Uploading..." : isUpdating ? "Update Image" : "Upload Image"}
          </button>
        )}
        <p className="file-info">
          {file
            ? `Selected: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`
            : currentImageUrl
            ? "Passport Photo Loaded ✓"
            : "No file selected"}
        </p>
      </div>
    </div>
  );
};

export default StudentImageUpload;